import { DownloadCloud } from "@lucide/vue";
import { useToast, POSITION as TOAST_POSITION } from "vue-toastification";
import { i18n } from "@/i18n.js";
import { showConfirm, showError } from "@core/ui/alert/alert";
import { didAnythingChange } from "./helpers/data/UnloadGuard";

import type { App } from "vue";

const toast = useToast();

// ==============================
// === ELECTRON-SPECIFIC CODE ===
// ==============================

/**
 * This code is basically "glue code" between the electron signals
 * that communicate between the app and the main js software.
 * Handles the commands send by the main code of the app.
 */

export default function (app: App) {
  // this code is only activated if the software is run as part of the app,
  // in the electron environment.
  if (!window.electron) {
    return;
  }
  const electron = window.electron;
  let downloadingToastId: number | string | null = null;

  /**
   * When an update is available, the user should be notified that
   * it is beeing downloaded.
   */
  electron.on("update-available", () => {
    downloadingToastId = toast.info(
      " " + i18n.t("texts.updateBeeingDownloaded"),
      {
        icon: DownloadCloud,
        position: TOAST_POSITION.BOTTOM_RIGHT,
        timeout: false
      }
    );
  });

  /**
   * As soon as an update is completely downloaded, ask if they want
   * to install it now or later. Send that choice to the app code.
   */
  electron.on("update-downloaded", async function (releaseName: string) {
    if (downloadingToastId != null) {
      toast.dismiss(downloadingToastId);
      downloadingToastId = null;
    }
    const installNow = await showConfirm(
      i18n.t("questions.closeAndInstall", { releaseName }),
      i18n.t("labels.yesInstallNow"),
      i18n.t("labels.noInstallLater")
    );
    if (installNow) {
      electron.send("update-now");
    }
  });

  /**
   * If an error occurred while updating, show a (generic) error message
   */
  electron.on("update-error", async function (message: string) {
    if (downloadingToastId != null) {
      toast.dismiss(downloadingToastId);
      downloadingToastId = null;
      showError(i18n.t("errors.updateFailed"));
    }

    // log the exception
    if (
      message.includes("System.Net.WebException") &&
      message.includes("500")
    ) {
      console.log("Error 500, System.Net.WebException.");
    } else {
      console.error("update error", message);
    }
  });

  /**
   * Confirm closing the app inside the software itself - so that we can
   * check where the user currently is and if it is safe to close it
   * directly.
   */
  electron.on("app-close", async function () {
    let closeNow = true;

    const route = app.config.globalProperties.$route;

    // ask if we really want to close if we are in software
    if (route.meta.isEditorView && didAnythingChange()) {
      closeNow = await showConfirm(
        i18n.t("questions.closeTab"),
        i18n.t("labels.closeTab"),
        i18n.t("labels.abort")
      );
    }

    if (closeNow) {
      electron.send("close");
    } else {
      electron.send("abort-close");
    }
  });

  electron.on("navigate-to", async function (newHash: string) {
    window.location.hash = newHash;
  });

  /**
   * Generic log function for testing.
   */
  electron.on("log", function (text: string) {
    console.log("[electron]:", text);
  });
}

declare global {
  // eslint-disable-next-line no-var
  var electron:
    | undefined
    | {
        encryptCredential: (string: string) => Promise<string>;
        decryptCredential: (string: string) => Promise<string>;
        on: (event: string, callback: (...args: string[]) => void) => void;
        send: (event: string, ...args: string[]) => void;
        getLoadedModel: () => Promise<string>;
      };
}
